import React, { createContext, useContext, useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { useAuth, db } from './AuthProvider';
import { SystemMetadata, Permission } from '../types';

interface PermissionContextType {
  can: (permission: Permission) => boolean;
  roleName: string | null;
  permissions: string[];
  isLoading: boolean;
  isMasterAdmin: boolean; 
  isLocalAdmin: boolean;  
}

const PermissionContext = createContext<PermissionContextType | undefined>(undefined);

const APP_ID = 'calendar';
const MASTER_APP_ID = 'master';
// Identificador soberano e exato do Ecosystem Overlord
const MASTER_EMAIL = 'pastordanielpcunha@gmail.com'; 

const DEFAULT_ROLE_PERMISSIONS: Record<string, Permission[]> = {
  'Administrador': [
    'events:read', 'events:create', 'events:update', 'events:delete', 
    'settings:write', 'users:manage'
  ],
  'Líder': ['events:read', 'events:create', 'events:update', 'events:delete'],
  'Membro': ['events:read']
};

export const PermissionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { profile, loading: authLoading } = useAuth();
  const [systemMetadata, setSystemMetadata] = useState<SystemMetadata | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchMetadata = async () => {
      try {
        const metaDoc = await getDoc(doc(db, 'system_metadata', APP_ID));
        if (metaDoc.exists()) {
          setSystemMetadata(metaDoc.data() as SystemMetadata);
        }
      } catch (error) {
        console.error("Erro ao buscar metadados:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (profile) fetchMetadata();
    else if (!authLoading) setIsLoading(false);
  }, [profile, authLoading]);

  // Lógica de Hierarquia Soberana (Ecosystem Overlord)
  // O Master Admin tem bypass total baseado no email oficial ou papel global
  const isMasterByEmail = profile?.email?.toLowerCase() === MASTER_EMAIL.toLowerCase();
                          
  const isMasterAdmin = isMasterByEmail || profile?.appRoles?.[MASTER_APP_ID] === 'Administrador';
  
  const roleName = profile?.appRoles?.[APP_ID] || 'Membro';
  const isLocalAdmin = roleName === 'Administrador';
  
  const permissions = systemMetadata?.roles?.[roleName] || DEFAULT_ROLE_PERMISSIONS[roleName] || [];

  const can = (permission: Permission) => {
    // God Mode: O Overlord ignora todas as travas de permissão
    if (isMasterAdmin) return true;
    
    // Local Admin: Autoridade máxima no contexto deste App
    if (isLocalAdmin) return true;
    
    return permissions.includes(permission);
  };

  return (
    <PermissionContext.Provider value={{ 
      can, 
      roleName, 
      permissions, 
      isLoading: authLoading || isLoading,
      isMasterAdmin,
      isLocalAdmin
    }}>
      {children}
    </PermissionContext.Provider>
  );
};

export const usePermission = () => {
  const context = useContext(PermissionContext);
  if (!context) throw new Error("usePermission deve ser usado dentro de um PermissionProvider");
  return context;
};